/**
 * A playful, ever-changing status word for the "still working" banner —
 * in the spirit of Claude Code's rotating gerunds. Cycles through a curated
 * list while the agent is busy so the user has something fun to watch (and a
 * clear "still going" signal) instead of a static "Writing the answer…".
 */

import { useEffect, useState } from 'react'

// Whimsical gerunds with a few network-monitoring-flavored ones mixed in to
// stay on-brand for Netwatch. Keep them friendly and PG.
const WORDS = [
  'Pondering',
  'Cogitating',
  'Noodling',
  'Ruminating',
  'Sleuthing',
  'Percolating',
  'Mulling',
  'Triangulating',
  'Conjuring',
  'Puzzling',
  'Correlating',
  'Untangling',
  'Marinating',
  'Tinkering',
  'Scheming',
  'Brewing',
  'Wrangling',
  'Probing',
  'Musing',
  'Synthesizing',
  'Unifying',
  'Analyzing',
  'Deliberating',
  'Finagling',
  'Spelunking',
  'Cross-referencing',
  'Divining',
  'Scanning',
  'Connecting dots',
  'Sniffing packets',
  'Reticulating splines',
]

const randomWord = (): string => WORDS[Math.floor(Math.random() * WORDS.length)]

// Pick a fresh word, avoiding an immediate repeat so the text visibly changes.
function nextWord(prev: string): string {
  if (WORDS.length < 2) return WORDS[0]
  let word = randomWord()
  while (word === prev) word = randomWord()
  return word
}

interface Props {
  intervalMs?: number
}

export function ThinkingLabel({ intervalMs = 2400 }: Props) {
  const [word, setWord] = useState(randomWord)

  useEffect(() => {
    const id = setInterval(() => setWord(nextWord), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])

  // key={word} re-triggers the fade each time the word changes.
  return (
    <span key={word} className="nw-status-word">
      {word}…
    </span>
  )
}
